import { z } from 'zod';

/**
 * Billing: the payment side of a module-bundle purchase.
 *
 * `Subscription`, `SubscriptionModule` and `TenantEntitlement` are
 * entitlement-service's data, modeled once in `entitlements.prisma` —
 * billing never duplicates them. This service owns only the payment
 * provider integration and calls `EntitlementService.subscribeToModules()`
 * once a payment succeeds.
 */
export const checkoutRequestSchema = z.object({
  /** The complete desired module bundle — an empty array is valid (the free baseline only). */
  moduleKeys: z.array(z.string().trim().toUpperCase().min(1).max(50)),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

export const CHECKOUT_STATUSES = ['PENDING', 'SUCCEEDED'] as const;
export type CheckoutStatus = (typeof CHECKOUT_STATUSES)[number];

export interface CheckoutResponse {
  readonly status: CheckoutStatus;
  /** Where to send the browser to complete payment. Null once already `SUCCEEDED`. */
  readonly checkoutUrl: string | null;
  readonly provider: string;
}

export interface SubscriptionResponse {
  /** Purchased modules only — the free baseline (core + `PEOPLE`) is implicit. */
  readonly moduleKeys: readonly string[];
  readonly status: string;
  readonly trialEndsAt: string | null;
  readonly currentPeriodEnd: string | null;
  readonly provider: string | null;
}
